/**
 * Storage Security Check Utility
 *
 * Validates that no authentication tokens are stored in localStorage or sessionStorage.
 * This is a security measure to ensure tokens are only stored in HTTP-only cookies.
 */

interface StorageCheckResult {
  passed: boolean
  violations: string[]
  timestamp: string
}

/**
 * Patterns to detect authentication tokens in storage
 */
const TOKEN_PATTERNS = [
  /supabase/i,
  /sb-/i,
  /token/i,
  /session/i,
  /auth/i,
  /jwt/i,
  /access_token/i,
  /refresh_token/i
]

/**
 * Check if a key or value contains sensitive authentication data
 */
function containsSensitiveData(str: string): boolean {
  return TOKEN_PATTERNS.some(pattern => pattern.test(str))
}

/**
 * Scan storage for authentication tokens
 */
function scanStorage(storage: Storage, storageName: string): string[] {
  const violations: string[] = []

  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i)
      if (!key) continue

      // Check if key name contains sensitive patterns
      if (containsSensitiveData(key)) {
        violations.push(`${storageName}: Found sensitive key "${key}"`)
        continue
      }

      // Check if value contains sensitive patterns
      const value = storage.getItem(key)
      if (value && containsSensitiveData(value)) {
        violations.push(`${storageName}: Key "${key}" contains sensitive data`)
      }
    }
  } catch (error) {
    console.error(`Error scanning ${storageName}:`, error)
  }

  return violations
}

/**
 * Perform comprehensive storage security check
 */
export function checkStorageSecurity(): StorageCheckResult {
  const violations: string[] = []
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  // Only check localStorage in production (localhost is allowed to use it for development)
  if (!isLocalhost) {
    violations.push(...scanStorage(localStorage, 'localStorage'))
  }

  // Always check sessionStorage (should never be used for tokens)
  violations.push(...scanStorage(sessionStorage, 'sessionStorage'))

  return {
    passed: violations.length === 0,
    violations,
    timestamp: new Date().toISOString()
  }
}

/**
 * Enable continuous storage monitoring (development only)
 */
export function enableStorageMonitoring(intervalMs: number = 5000): () => void {
  if (import.meta.env.PROD) {
    console.warn('Storage monitoring should only be enabled in development')
    return () => {}
  }

  console.log('🔒 Storage security monitoring enabled')

  const intervalId = setInterval(() => {
    const result = checkStorageSecurity()

    if (!result.passed) {
      console.error('🚨 SECURITY VIOLATION: Tokens detected in browser storage!', result.violations)
    }
  }, intervalMs)

  // Return cleanup function
  return () => {
    clearInterval(intervalId)
    console.log('🔒 Storage security monitoring disabled')
  }
}

/**
 * One-time storage check with console output
 */
export function performStorageCheck(): void {
  const result = checkStorageSecurity()

  if (result.passed) {
    console.log('✅ Storage security check passed - no tokens in localStorage/sessionStorage')
  } else {
    console.error('❌ Storage security check FAILED:', result.violations)
  }
}
